import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Bell, CheckCheck, Inbox, AlertTriangle, CheckCircle2, Info } from 'lucide-react';
import { Link } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL + '/api/notifications';

const CATEGORY_TINT = {
  approvals: 'bg-blue-50 text-blue-700',
  payroll: 'bg-emerald-50 text-emerald-700',
  statutory: 'bg-purple-50 text-purple-700',
  lifecycle: 'bg-indigo-50 text-indigo-700',
  attendance: 'bg-amber-50 text-amber-700',
  security: 'bg-rose-50 text-rose-700',
  system: 'bg-slate-100 text-slate-700',
};

const SEVERITY_ICON = {
  info: Info,
  success: CheckCircle2,
  warning: AlertTriangle,
  critical: AlertTriangle,
};

const SEVERITY_COLOR = {
  info: 'text-blue-500',
  success: 'text-emerald-500',
  warning: 'text-amber-500',
  critical: 'text-rose-600',
};

const formatTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
};

/**
 * Bell dropdown: latest 7 notifications + total unread + "View all" link.
 * Polls /unread-count every 60s in background.
 */
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

export function getNotificationTargetUrl(n, userRole) {
  const title = (n.title || '').toLowerCase();
  const cat = (n.category || '').toLowerCase();
  const type = (n.type || n.metadata?.type || '').toLowerCase();
  const recordId = n.metadata?.item_id || n.metadata?.record_id || n.metadata?.id || '';

  const isEmployee = userRole === 'employee';
  const isAdmin = ['tenant_admin', 'hr_admin', 'payroll_admin', 'super_admin'].includes(userRole);
  const isManager = userRole === 'department_manager' || userRole === 'manager';

  // 1. Regularization
  if (title.includes('regularization') || type.includes('regularization') || cat.includes('regularization')) {
    return isAdmin || isManager
      ? `/hr/ess-approvals?tab=regularization${recordId ? `&id=${recordId}` : ''}`
      : `/ess/leave-attendance?tab=regularization${recordId ? `&id=${recordId}` : ''}`;
  }
  // 2. Shift Request
  if (title.includes('shift') || type.includes('shift') || cat.includes('shift')) {
    return isAdmin || isManager
      ? `/hr/ess-approvals?tab=shift${recordId ? `&id=${recordId}` : ''}`
      : `/ess/leave-attendance?tab=shift${recordId ? `&id=${recordId}` : ''}`;
  }
  // 3. Comp-Off
  if (title.includes('comp-off') || title.includes('compoff') || type.includes('compoff')) {
    return isAdmin || isManager
      ? `/hr/ess-approvals?tab=compoff${recordId ? `&id=${recordId}` : ''}`
      : `/ess/leave-attendance?tab=compoff${recordId ? `&id=${recordId}` : ''}`;
  }
  // 4. Leave Request / Leave Applied / Approved / Rejected
  if (title.includes('leave') || type.includes('leave') || cat.includes('leave')) {
    return isAdmin || isManager
      ? `/hr/ess-approvals?tab=leave${recordId ? `&id=${recordId}` : ''}`
      : `/ess/leave-attendance?tab=leave${recordId ? `&id=${recordId}` : ''}`;
  }
  // 5. KYC Pending / Confirmation
  if (title.includes('kyc') || type.includes('kyc')) {
    return isAdmin ? `/payroll/kyc-corrections` : `/ess/kyc`;
  }
  // 6. Expense Claim Approved / Expense / Reimbursement
  if (title.includes('expense') || title.includes('reimbursement') || type.includes('expense') || type.includes('reimbursement')) {
    return isAdmin
      ? `/payroll/expense-claims-admin${recordId ? `?id=${recordId}` : ''}`
      : `/ess/expense-claims${recordId ? `?id=${recordId}` : ''}`;
  }
  // 7. Advance Approved / Advance / Loan
  if (title.includes('advance') || title.includes('loan') || type.includes('advance') || type.includes('loan')) {
    return isAdmin
      ? `/payroll/loans`
      : `/ess/advance-ledger`;
  }
  // 8. Document Request / Document
  if (title.includes('document') || title.includes('doc') || type.includes('document')) {
    return `/ess/documents`;
  }
  // 9. Asset Assigned / Asset
  if (title.includes('asset') || type.includes('asset')) {
    return isEmployee ? `/ess/assets-docs` : `/employees`;
  }
  // 10. Payslip Released / Payslip
  if (title.includes('payslip') || type.includes('payslip')) {
    return `/ess/pay-tax?tab=payslips`;
  }
  // 11. Tax Summary Available / Tax Document / Form 16 / Declaration
  if (title.includes('tax') || title.includes('form 16') || title.includes('form16') || title.includes('declaration')) {
    return `/ess/pay-tax?tab=tax_summary`;
  }
  // 12. HR Bench / Increments / Helpdesk
  if (title.includes('new joiner') || title.includes('onboarding')) return `/hr-bench?tab=new_joiner`;
  if (title.includes('resignation') || title.includes('exit')) return `/hr-bench?tab=resignation`;
  if (title.includes('hr bench') || type.includes('hr_bench')) return `/hr-bench`;
  if (title.includes('increment') || type.includes('increment')) return `/hr/increment-workbench`;
  if (title.includes('payroll') || type.includes('payroll')) return isAdmin ? `/payroll/run` : `/ess/pay-tax?tab=payslips`;
  if (title.includes('ticket') || title.includes('helpdesk')) return `/ess/helpdesk${recordId ? `?id=${recordId}` : ''}`;

  return n.link || (isEmployee ? '/ess/portal' : '/dashboard');
}

const NotificationBell = () => {
  const { user } = useAuth();
  const userRole = (user?.role || '').toLowerCase();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);

  const fetchUnread = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/unread-count`, { withCredentials: true });
      setUnread(data.total || 0);
    } catch (_) { /* silent */ }
  }, []);

  const fetchLatest = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}?limit=7`, { withCredentials: true });
      setItems(data || []);
    } catch (_) { setItems([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchUnread();
    const t = setInterval(fetchUnread, 60000);
    return () => clearInterval(t);
  }, [fetchUnread]);

  useEffect(() => {
    if (open) fetchLatest();
  }, [open, fetchLatest]);

  useEffect(() => {
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const markRead = async (n, e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    try {
      await axios.patch(`${API}/${n.id}/read`, null, { withCredentials: true });
      setItems((arr) => arr.map((x) => (x.id === n.id ? { ...x, is_read: true } : x)));
      setUnread((c) => Math.max(0, c - 1));
    } catch (_) { /* silent */ }
  };

  const markAll = async () => {
    try {
      await axios.post(`${API}/mark-all-read`, null, { withCredentials: true });
      setItems((arr) => arr.map((x) => ({ ...x, is_read: true })));
      setUnread(0);
    } catch (_) { /* silent */ }
  };

  const onItemClick = async (n) => {
    const targetUrl = getNotificationTargetUrl(n, userRole);
    setOpen(false);

    // Permission check for protected routes
    const isEmployee = userRole === 'employee';
    const isProtectedAdminRoute = ['/salary-increment-workbench', '/payroll-execution', '/hr-bench', '/kyc-corrections'].some(r => targetUrl.startsWith(r));
    
    if (isEmployee && isProtectedAdminRoute) {
      toast.error('Access Denied: You do not have permission to access this screen.');
      return;
    }

    try {
      navigate(targetUrl);
      if (!n.is_read) {
        await markRead(n);
      }
    } catch (err) {
      toast.error('Failed to navigate to target screen');
    }
  };

  return (
    <div className="relative" ref={wrapRef}>
      <button
        data-testid="notifications-button"
        className="relative p-2 hover:bg-slate-100 rounded-md transition-colors"
        onClick={() => setOpen((o) => !o)}
      >
        <Bell className="w-5 h-5 text-slate-600" strokeWidth={1.5} />
        {unread > 0 && (
          <span data-testid="notification-badge" className="absolute -top-1 -right-1 bg-red-500 text-white text-xs min-w-[20px] h-5 px-1 flex items-center justify-center rounded-full font-medium">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div data-testid="notification-dropdown" className="absolute right-0 mt-2 w-96 bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden z-50">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <div>
              <h3 className="text-sm font-semibold text-slate-900">Notifications</h3>
              <p className="text-xs text-slate-500">{unread > 0 ? `${unread} unread` : 'All caught up'}</p>
            </div>
            {unread > 0 && (
              <button
                data-testid="mark-all-read-btn"
                onClick={markAll}
                className="text-xs text-[#0052FF] hover:underline flex items-center gap-1"
              >
                <CheckCheck className="w-3.5 h-3.5" /> Mark all read
              </button>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <p className="text-center text-sm text-slate-400 py-8">Loading...</p>
            ) : items.length === 0 ? (
              <div className="text-center py-10 text-slate-400">
                <Inbox className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                <p className="text-sm">No notifications yet</p>
              </div>
            ) : items.map((n) => {
              const Icon = SEVERITY_ICON[n.severity] || Info;
              return (
                <button
                  key={n.id}
                  data-testid={`notif-item-${n.id}`}
                  onClick={() => onItemClick(n)}
                  className={`w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-slate-50 flex items-start gap-3 ${!n.is_read ? 'bg-blue-50/40' : ''}`}
                >
                  <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${SEVERITY_COLOR[n.severity] || 'text-slate-400'}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-medium text-slate-900 truncate">{n.title}</p>
                      {!n.is_read && <span className="w-2 h-2 rounded-full bg-blue-500 shrink-0" />}
                    </div>
                    <p className="text-xs text-slate-600 mt-0.5 line-clamp-2">{n.message}</p>
                    <div className="flex items-center gap-2 mt-1.5">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${CATEGORY_TINT[n.category] || CATEGORY_TINT.system}`}>
                        {n.category}
                      </span>
                      <span className="text-[10px] text-slate-400">{formatTime(n.created_at)}</span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          <div className="border-t border-slate-100 px-4 py-2 flex items-center justify-between text-xs">
            <Link to="/notifications" onClick={() => setOpen(false)} className="text-[#0052FF] hover:underline" data-testid="view-all-notifs-link">
              View all notifications
            </Link>
            <Link to="/notifications/preferences" onClick={() => setOpen(false)} className="text-slate-500 hover:text-slate-700">
              Preferences
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
